/**
 * A page of company lists with its heading, count and actions. Used twice,
 * like People Lists' `ImportsListView`: on its own page (Research ▸ Company
 * Lists, every list in the firm) and as the Company Lists tab of a search.
 *
 * Creating a list needs a search (00 Q1: the UI always starts inside one), so
 * "New list" and "Import CSV" only appear when `searchId` is set.
 *
 * Filters, the People Lists way (`ImportsListView` / `ImportsToolbar`):
 * - a debounced name box (`q`) on both surfaces;
 * - on the firm page only, a searches typeahead plus a removable chip. The
 *   page owns that filter (it lives in the URL) and hands it in through
 *   `searchFilter`, whose PRESENCE is what renders both controls. A search's
 *   tab omits it: its search is pinned, and a control that could point the
 *   tab at another search would make it lie about its own contents.
 * Any filter change goes back to page 1.
 *
 * `urlFilters` (the firm page) keeps `q`, `owner` and `page` in the URL, as
 * People Lists' firm page does; the search tab keeps them in component state,
 * as People Lists' Search Detail tab does. See `useCompanyListsFilters`.
 *
 * Also from People Lists (Trello #831): the "Imported by: Me ▾" scope menu
 * (`ImportOwnerMenu`, its options from `/company-lists/owners`) on BOTH
 * surfaces, as `ImportsListView` renders it on both — the host picks the
 * starting scope; the sortable columns, sorted server-side so the order holds
 * across pages, Date newest first by default, the sort held where the filters
 * are (URL on the firm page, state on the tab); and the row's download icon (the list's CSV).
 */
import type { CompanyListSummary } from "@nu-assist/contracts";
import { listPageSize } from "@nu-assist/shared-fe";
import { Button, Spinner } from "@nu-assist/ui";
import { FileUp, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import type { CompanyListsClient } from "../../clients/companyLists.client";
import type { ImportsClient } from "../../clients/imports.client";
import { TablePagination } from "../../components/TablePagination";
import { ImportOwnerMenu } from "../../routes/research/imports/ImportOwnerMenu";
import { ImportOwnersError } from "../../routes/research/imports/ImportOwnersError";
import {
  SearchFilterChip,
  resolveSearchFilterLabel,
} from "../../routes/research/imports/SearchFilterChip";
import { PickerSearchFilterInput } from "../imports/selection/PickerSearchFilterInput";
import { CompanyListDialog, type CompanyListDialogMode } from "./CompanyListDialog";
import { CompanyListsTable } from "./CompanyListsTable";
import { useCompanyListOwners, useCompanyLists } from "./queries";
import { saveBlob } from "./saveBlob";
import { useCompanyListsFilters } from "./useCompanyListsFilters";

/** Same debounce as People Lists' title box (`ImportsListView`). */
export const NAME_FILTER_DEBOUNCE_MS = 300;

/** The firm page's search filter: the URL-held value plus its controls. */
export interface CompanyListsSearchFilter {
  /** The typeahead lists Thrive searches through the imports client. */
  importsClient: ImportsClient;
  /** The applied search, or null for every search. */
  searchIdThrive: string | null;
  /** Display-only name carried in the URL, for the chip. */
  searchName: string | null;
  onApply: (searchId: string, searchName: string | null) => void;
  onClear: () => void;
}

interface CompanyListsViewBaseProps {
  client: CompanyListsClient;
  /** Null = every list in the firm. */
  searchId: string | null;
  title?: string;
  headingLevel?: "h1" | "h2";
  /** The caller's email: the menu's "Me" and the own-row accent. */
  meEmail?: string;
  /** `you` | `all` | an email. The firm page starts at `you` (or `all` when it
   *  arrives with a search filter); a search's tab at `all`, as People Lists. */
  initialOwner?: string;
}

/**
 * `urlFilters` keeps the name filter and page in the URL (`q`, `page`).
 * Opt-in: the firm page passes it; a search's tab must not, like People Lists'
 * tab.
 *
 * `searchFilter` (firm page only, `searchId === null`) REQUIRES `urlFilters`,
 * and the type says so: the page's own URL write drops `page` together with
 * the search filter, which is the only page reset a search-filter change
 * gets. A state-mode view with a search filter would keep a stale page.
 */
type CompanyListsViewProps = CompanyListsViewBaseProps &
  (
    | { searchFilter: CompanyListsSearchFilter; urlFilters: true }
    | { searchFilter?: undefined; urlFilters?: boolean }
  );

export function CompanyListsView({
  client,
  searchId,
  title = "Company Lists",
  headingLevel = "h1",
  searchFilter,
  urlFilters = false,
  meEmail = "",
  initialOwner = "all",
}: CompanyListsViewProps) {
  const pageSize = listPageSize();
  const [dialog, setDialog] = useState<CompanyListDialogMode | null>(null);
  // The raw box, the debounced and trimmed term the query uses, the scope and
  // the page: in the URL or in component state, depending on `urlFilters`.
  const {
    searchText,
    q,
    page,
    owner,
    sort,
    setSearchText,
    commitQ,
    goToPage,
    setOwner,
    toggleSort,
  } = useCompanyListsFilters({ persist: urlFilters, initialOwner });
  const { query: ownersQuery, owners } = useCompanyListOwners(client);
  const filterSearchId = searchId === null ? (searchFilter?.searchIdThrive ?? null) : null;

  // Debounce the box into `q`, resetting to page 1 in the same update. The
  // guard compares trimmed with trimmed, as People Lists does, so the mount
  // run and a trailing space do not commit (and eat a pager click).
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchText.trim() === q) return;
      commitQ(searchText);
    }, NAME_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchText, q, commitQ]);

  // A search-filter change needs no page reset here: `searchFilter` only
  // comes with `urlFilters` (see the props type), and `CompanyListsPage`
  // drops `page` in the same URL write that changes the filter.

  const query = useCompanyLists({
    client,
    searchId,
    page,
    pageSize,
    q,
    searchIdThrive: filterSearchId,
    owner,
    sort,
  });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const Heading = headingLevel;

  // The index can shrink under the open page (a list deleted elsewhere, e.g.
  // through the API): step back to the new last page.
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (query.isSuccess && !query.isFetching && page > pageCount) goToPage(pageCount);
  }, [query.isSuccess, query.isFetching, page, pageCount, goToPage]);

  async function handleDownload(row: CompanyListSummary) {
    try {
      const { filename, blob } = await client.exportCsv(row.list_id);
      saveBlob(filename, blob);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn’t export.");
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="company-lists-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <Heading className={headingLevel === "h1" ? "text-h1 text-text" : "text-h2 text-text"}>
            {title}
          </Heading>
          <span className="text-label text-text-muted" data-testid="company-lists-count">
            ({total.toLocaleString()})
          </span>
        </div>
        <div className="flex items-center gap-3">
          <ImportOwnerMenu
            value={owner}
            meEmail={meEmail}
            owners={owners}
            onChange={setOwner}
            ariaLabel="Change company list scope"
          />
          {searchId !== null && (
            <>
              <Button
                variant="secondary"
                onClick={() => setDialog("import")}
                data-testid="company-lists-import"
              >
                <FileUp className="h-4 w-4" aria-hidden="true" />
                Import CSV
              </Button>
              <Button onClick={() => setDialog("new")} data-testid="company-lists-new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New list
              </Button>
            </>
          )}
        </div>
      </div>

      {searchId === null && (
        <p className="text-body text-text-secondary">
          Every company list, whichever search made it. Open a search to add a new one.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-60 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by company list name or search name"
            aria-label="Search by company list name or search name"
            data-testid="company-lists-search"
            className="w-full rounded-md border border-border bg-surface py-1.5 pl-9 pr-3 text-body text-text placeholder:text-text-muted hover:border-border-hover focus:border-border-hover focus:outline-none"
          />
        </div>
        {/* The second box matches SEARCHES and applies one as a filter; the
            visible label tells the two boxes apart, as on People Lists. */}
        {searchId === null && searchFilter ? (
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-label text-text-secondary">
              Filter by search:
            </span>
            <PickerSearchFilterInput
              client={searchFilter.importsClient}
              appliedSearchId={searchFilter.searchIdThrive}
              onApply={searchFilter.onApply}
            />
          </div>
        ) : null}
      </div>

      {searchFilter && filterSearchId ? (
        <SearchFilterChip
          label={resolveSearchFilterLabel({
            searchId: filterSearchId,
            nameFromUrl: searchFilter.searchName,
            items,
          })}
          onClear={searchFilter.onClear}
        />
      ) : null}

      <ImportOwnersError query={ownersQuery} variant="banner" />

      {/* Also while a filter change is in flight with nothing to show yet:
          `placeholderData` keeps the previous (empty) page, and showing its
          empty state under the NEW filter flag would flash the wrong one
          ("No company lists yet" right after clearing a no-match filter). */}
      {query.isPending || (query.isPlaceholderData && items.length === 0) ? (
        <div className="flex justify-center py-12">
          <Spinner label="Loading company lists…" />
        </div>
      ) : query.isError && !query.data ? (
        <div
          data-testid="company-lists-error"
          className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
        >
          Couldn’t load company lists. Please try again.
        </div>
      ) : (
        <>
          <CompanyListsTable
            items={items}
            showSearch={searchId === null}
            filtered={q !== "" || filterSearchId !== null || owner !== "all"}
            meEmail={meEmail}
            sort={sort}
            onSort={toggleSort}
            onDownload={handleDownload}
          />
          <TablePagination
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={goToPage}
            noun="company list"
            testId="company-lists-pagination"
          />
        </>
      )}

      {dialog !== null && searchId !== null && (
        <CompanyListDialog
          mode={dialog}
          client={client}
          searchId={searchId}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
